import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CosmeticOptions, CustomRule, Displayable } from '../../models/filter-models';
import { Color, MinimapIconShape, MinimapIconSize, Rarity } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { CosmeticControls } from '../cosmetic-controls.model';
import { LabelViewerComponent } from '../../common/label-viewer/label-viewer.component';
import { MinimapIconComponent } from '../../common/icons/minimap-icon/minimap-icon.component';
import { HighlightBeamIconComponent } from '../../common/icons/highlight-beam-icon/highlight-beam-icon.component';
import { BaseComponent } from '../../common/base.component';

@Component({
  selector: '[app-cosmetic-controls]',
  imports: [FormsModule, LabelViewerComponent, MinimapIconComponent, HighlightBeamIconComponent],
  templateUrl: './cosmetic-controls.component.html',
  styleUrl: './cosmetic-controls.component.scss'
})
export class CosmeticControlsComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) controls!: CosmeticControls;
  @Input({ required: true }) displayable!: Displayable;
  @Input({ required: true }) isCurrency!: boolean;
  @Input({ required: true }) disabled!: boolean;
  @Input() forceHidden?: boolean;
  @Input() exampleName?: string;
  @Input() rarity?: Rarity;
  @Input() isWaystone?: boolean;
  @Input() isPoe1Gem?: boolean;
  @Input() isDivinationCard?: boolean;
  @Input() sockets?: string;
  @Input() influence?: string;
  @Input() fractured?: boolean;
  @Input() synthesised?: boolean;
  @Input() veiled?: boolean;
  @Input() rule?: CustomRule;
  @Input() dirtyForceUpdate?: number;

  @Output('updateFilter') updateFilterEmitter = new EventEmitter<void>();

  defaultCosmetics = new CosmeticOptions();

  highlightExample = false;

  Color = Color;
  MinimapIconSize = MinimapIconSize;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.checkForChanges();
  }

  toggleHighlightOptionsOpen() {
    this.controls.highlightOptionsOpen = !this.controls.highlightOptionsOpen;
    if (!this.displayable.displayOptions.cosmeticOptions?.highlight && this.controls.highlightOptionsOpen) this.toggleHighlight();
  }

  toggleSoundOptionsOpen() {
    this.controls.soundOptionsOpen = !this.controls.soundOptionsOpen;

    if (this.controls.soundOptionsOpen && this.displayable.displayOptions.cosmeticOptions?.alertSoundSound !== undefined) {
      this.playSound(this.displayable.displayOptions.cosmeticOptions?.alertSoundSound);
    }

    if (!this.displayable.displayOptions.cosmeticOptions?.alertSound && this.controls.soundOptionsOpen) {
      this.toggleSound();
    }
  }

  playSound(sound: string | null) {
    if (!sound) return;
    const audio = new Audio();
    audio.src = `/sounds/sound${sound}.mp3`;
    audio.load();
    audio.play();
  }

  toggleHighlight() {
    this.displayable.displayOptions.cosmeticOptions!.highlight = !this.displayable.displayOptions.cosmeticOptions!.highlight;
    if (this.displayable.displayOptions.cosmeticOptions!.highlight) {
      this.displayable.displayOptions.cosmeticOptions!.beam = true;
      this.displayable.displayOptions.cosmeticOptions!.highlightColor = Color.White;
      this.displayable.displayOptions.cosmeticOptions!.minimapIconSize = MinimapIconSize.Small;
      this.displayable.displayOptions.cosmeticOptions!.minimapIconShape = MinimapIconShape.Circle;
      if (this.rule?.continue) this.rule.continue = false;
    }
    this.updateFilter();
  }

  toggleSound() {
    this.displayable.displayOptions.cosmeticOptions!.alertSound = !this.displayable.displayOptions.cosmeticOptions!.alertSound;
    if (this.displayable.displayOptions.cosmeticOptions!.alertSound) {
      this.displayable.displayOptions.cosmeticOptions!.alertSoundSound = '2';
      this.displayable.displayOptions.cosmeticOptions!.alertSoundVolume = 200;
    }
    this.updateFilter();
  }

  checkForLabelChanges() {
    this.controls.hasChangedLabel =
      (this.displayable.displayOptions.cosmeticOptions!.backgroundColor ?? null) !== (this.defaultCosmetics.backgroundColor ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.borderColor ?? null) !== (this.defaultCosmetics.borderColor ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.textColor ?? null) !== (this.defaultCosmetics.textColor ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.fontSize ?? null) !== (this.defaultCosmetics.fontSize ?? null);
    return this.controls.hasChangedLabel;
  }

  checkForIconChanges() {
    this.controls.hasChangedIcon =
      (this.displayable.displayOptions.cosmeticOptions!.highlight ?? null) !== (this.defaultCosmetics.highlight ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.highlightColor ?? null) !== (this.defaultCosmetics.highlightColor ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.minimapIconSize ?? null) !== (this.defaultCosmetics.minimapIconSize ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.minimapIconShape ?? null) !== (this.defaultCosmetics.minimapIconShape ?? null);
    return this.controls.hasChangedIcon;
  }

  checkForSoundChanges() {
    this.controls.hasChangedSound =
      (this.displayable.displayOptions.cosmeticOptions!.alertSound ?? null) !== (this.defaultCosmetics.alertSound ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.alertSoundSound ?? null) !== (this.defaultCosmetics.alertSoundSound ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.alertSoundVolume ?? null) !== (this.defaultCosmetics.alertSoundVolume ?? null) ||
      (this.displayable.displayOptions.cosmeticOptions!.disableDropSound ?? null) !== (this.defaultCosmetics.disableDropSound ?? null);
    return this.controls.hasChangedSound;
  }

  checkForChanges() {
    this.checkForLabelChanges();
    this.checkForIconChanges();
    this.checkForSoundChanges();
  }

  updateFilter() {
    this.checkForChanges();
    this.updateFilterEmitter.emit();
  }
}
